import classnames from 'classnames';
import { useSelector } from 'react-redux';

import makeStyles from '@material-ui/core/styles/makeStyles';
import _map from 'lodash/map';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import config from '../common/config';
import { color, fontSize, s8sColor } from '../common/themes';
import Button from './Button';
import IconExpand from './IconExpand';
import IconX from './IconX';
import ImageDisplay from './ImageDisplay';
import useResponsiveScale from './hooks/useResponsiveScale';

import icSuperBotX from '../assets/images/png/ic_superbotx_logo.png';
import icSuper8 from '../assets/images/png/s8s_logo.png';

import icFbMessenger02 from '../assets/images/svg/ic-fbmessenger-02.svg';
import icLine from '../assets/images/svg/ic-line.svg';

import imgInstagram20 from '../assets/images/png/ic-instagram@2x.png';
import icMail from '../assets/images/svg/ic_mail_24.svg';
import icMobile from '../assets/images/svg/ic_mobile_24.svg';
import LivechatService from '../services/LivechatService';

import { getMessageSummary } from '../common/Util';

const useStyles = makeStyles(
	() => ({
		chat: (props: any) => ({
			display: 'flex',
			flexDirection: 'column',
			borderRadius: props.isMobile ? 'none' : '12px',
			backgroundColor: color.$7,
			height: props.isMobile ? '100vh' : props.isExpanded ? '100%' : props?.windowSize?.height || 580,
			width: props.isMobile ? '100vw' : '100%',
			// 不畫邊框：#EFEFEF 的 1px 邊框沿著白色內容區看不見，但沿著深色標題列會變成
			// 一條亮白細線（圓角處尤其明顯）。Chat 面板本來就沒有邊框，移除後兩者一致。
			maxHeight: props.isMobile
				? 'none'
				: props.isExpanded
				? '100%'
				: `calc(100vh - 110px - ${props?.styleData?.positionSettings?.bottom || 0}px)`,
			overflowY: 'auto',
		}),
		header: (props: any) => ({
			position: 'relative',
			padding: 24,
			borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
			borderTopLeftRadius: props.isMobile ? 'none' : '12px',
			borderTopRightRadius: props.isMobile ? 'none' : '12px',
			minheight: 70,
			height: 'fit-content',
			backgroundColor: s8sColor.neutral.black,
		}),
		x: {
			position: 'absolute',
			top: 12,
			right: 12,
			cursor: 'pointer',
		},
		expand: (props: any) => ({
			position: 'absolute',
			top: 12,
			right: props.alwaysOn ? 12 : 44,
			cursor: 'pointer',
		}),
		title: {
			fontSize: ({ fontScale }: any) => fontSize.lg * (fontScale || 1),
			fontWeight: 500,
			fontStretch: 'normal',
			fontStyle: 'normal',
			lineHeight: '1',
			marginBottom: 12,
			color: s8sColor.neutral.white,
		},
		description: {
			fontSize: ({ fontScale }: any) => fontSize.md * (fontScale || 1),
			fontWeight: 'normal',
			fontStretch: 'normal',
			fontStyle: 'normal',
			lineHeight: 'normal',
			color: s8sColor.neutral.white,
		},
		content: (props: any) => ({
			minHeight: `363px`,
			padding: 24,
			boxSizing: 'border-box',
			backgroundColor: color.$7,
			height: props.isMobile ? 'calc(100vh - 150px)' : '0',
			flex: props.isMobile ? 'unset' : 1,
		}),
		dialog: {
			padding: 16,
			borderColor: s8sColor.neutral.gray200,
			borderWidth: 1,
			borderStyle: 'solid',
			borderRadius: 12,
			backgroundColor: s8sColor.neutral.white,
		},
		text: {
			fontSize: ({ fontScale }: any) => fontSize.lg * (fontScale || 1),
			fontWeight: 'normal',
			fontStretch: 'normal',
			fontStyle: 'normal',
			lineHeight: '1',
			color: color.$13,
			marginBottom: 16,
		},
		fc: {
			display: 'flex',
			alignItems: 'center',
		},
		displayName: {
			fontSize: ({ fontScale }: any) => fontSize.md * (fontScale || 1),
			fontWeight: 'normal',
			fontStretch: 'normal',
			fontStyle: 'normal',
			lineHeight: '1',
			color: color.$13,
		},
		text2: {
			fontSize: ({ fontScale }: any) => fontSize.sm * (fontScale || 1),
			fontWeight: 'normal',
			fontStretch: 'normal',
			fontStyle: 'normal',
			lineHeight: '1',
			color: color.$12,
			textAlign: 'center',
			marginBottom: 12,
		},
		communitys: {
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			'& img': {
				width: 24,
				height: 24,
			},
			'& a + a': {
				marginLeft: 16,
			},
		},
		logo: {
			'& img': {
				borderRadius: '30px',
				boxShadow: '-5px 5px 8px 0 rgba(0, 0, 0, 0.2)',
				width: 60,
				height: 60,
			},
		},
		copyright: {
			fontSize: ({ fontScale }: any) => fontSize.sm * (fontScale || 1),
			fontWeight: 500,
			fontStretch: 'normal',
			fontStyle: 'normal',
			lineHeight: '1.38',
			display: 'flex',
			justifyContent: 'center',
			alignItems: 'center',
			height: 30,

			'& a': {
				color: s8sColor.neutral.gray800,
			},
		},
		lastMessage: {
			color: color.$12,
			fontSize: ({ fontScale }: any) => fontSize.sm * (fontScale || 1),
			lineHeight: 1.31,
			marginBottom: 16,
		},
		twoLineWrap: {
			display: '-webkit-box',
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			'-webkit-box-orient': 'vertical',
			'-webkit-line-clamp': '2',
			wordWrap: 'break-word',
		},
		borderRadius: {
			borderBottomLeftRadius: '12px',
			borderBottomRightRadius: '12px',
		},
		privacyPolicy: {
			marginTop: 24,
			color: '#333333',
			fontSize: ({ fontScale }: any) => fontSize.xs * (fontScale || 1),
			textAlign: 'center',
			'& > a': {
				color: '#333333',
				textDecoration: 'underline',
			},
		},
	}),
	{
		name: 'Preview',
	}
);

function Preview({
	onOpenChat,
	onClose,
	onToggleExpand,
	styleData,
	orgData,
	isGetLastMessage,
	isShowBottomInfo,
	isSuper8,
	alwaysOn,
}: any) {
	const isMobile = useSelector((store: any) => store.socketio.isMobile);
	const isExpanded = useSelector((store: any) => store.layout?.isExpanded);

	const { windowSize, fontScale } = useResponsiveScale();

	const classes = useStyles({ isMobile, styleData, windowSize, fontScale, isExpanded, alwaysOn });

	const { t } = useTranslation();

	const [lastMessage, setLastMessage]: any = useState(null);
	const orgId = useSelector((store: any) => store.socketio.orgId);
	const customerToken = useSelector((store: any) => store.socketio.customerToken);

	const {
		livechatTextColor,
		livechatBackgroundColor,
		title,
		content,
		buttonTextColor,
		buttonColor,
		contacts,
		hiddenPoweredBy,
		privacyPolicy,
	} = styleData || {};

	const iconMap: any = {
		line: icLine,
		facebook: icFbMessenger02,
		instagram: imgInstagram20,
		email: icMail,
		mobile: icMobile,
	};

	const contactsEnabled = Object.values(contacts).some((c: any) => c?.enabled);

	const getIconUrl = (data: string, key: string) => {
		let openUrl = data;
		switch (key) {
			case 'email':
				openUrl = `mailto:${data}`;
				break;
			case 'mobile':
				openUrl = `tel:${data}`;
				break;
			default:
		}

		return openUrl;
	};

	useEffect(() => {
		const getLastMessage = async () => {
			const response: any = await LivechatService.getMessages(orgId, customerToken, 0, 1);

			setLastMessage(response.results[0]);
		};
		if (customerToken && orgId && isGetLastMessage) {
			getLastMessage();
		}
	}, [customerToken, orgId, isGetLastMessage]);

	return (
		<div className={classes.chat}>
			<div className={classes.header} style={{ backgroundColor: livechatBackgroundColor }}>
				{!alwaysOn && <IconX id="x" onClick={() => onClose()} className={classes.x} color={color.$7} hoverColor={color.$7} />}
				{!isMobile && (
					<IconExpand
						id="expand"
						isExpanded={isExpanded}
						onClick={onToggleExpand}
						className={classes.expand}
						color={color.$7}
						hoverColor={color.$7}
					/>
				)}
				<div className={classes.title} style={{ color: livechatTextColor }}>
					{title}
				</div>
				<div className={classnames(classes.description, classes.twoLineWrap)} style={{ color: livechatTextColor }}>
					{content}
				</div>
			</div>
			<div className={classnames(classes.content, { [classes.borderRadius]: hiddenPoweredBy })}>
				<div className={classes.dialog}>
					<div className={classes.text}>{t('conversation')}</div>
					<div className={classes.fc} style={{ marginBottom: 16 }}>
						<ImageDisplay
							style={{
								borderRadius: '50%',
								marginRight: 12,
							}}
							src={orgData.icon}
							width={44}
							height={44}
						/>
						<div>
							<div className={classes.displayName}>{orgData.displayName}</div>
						</div>
					</div>
					{customerToken && lastMessage && (
						<div className={classnames(classes.lastMessage, classes.twoLineWrap)}>
							{getMessageSummary(lastMessage, orgData.displayName)}
						</div>
					)}
					<Button
						style={{
							marginBottom: 24,
							width: '100%',
							backgroundColor: buttonColor,
							color: buttonTextColor,
						}}
						topic="primaryGreen"
						onClick={() => onOpenChat()}
					>
						{lastMessage ? t('continueConv') : t('startConv')}
					</Button>
					{contactsEnabled && (
						<>
							<div className={classes.text2}>{t('orSwitch')}</div>
							<div className={classes.communitys}>
								{_map(contacts, (value, key) => {
									return (
										value?.enabled &&
										(key === 'email' ? (
											<a key={`${value.data}`} target="_parent" href={getIconUrl(value.data, key)}>
												<img src={iconMap[key]} alt={key} />
											</a>
										) : key === 'mobile' ? (
											<a key={`${value.data}`} href={`#${key}`} onClick={() => window.open(getIconUrl(value.data, key))}>
												<img src={iconMap[key]} alt={key} />
											</a>
										) : (
											<a key={`${value.data}`} target="_blank" href={getIconUrl(value.data, key)} rel="noreferrer">
												<img src={iconMap[key]} alt={key} />
											</a>
										))
									);
								})}
							</div>
						</>
					)}
					{privacyPolicy?.enabled && (
						<div className={classes.privacyPolicy}>
							{privacyPolicy?.text}
							<a href={privacyPolicy?.url} target="_blank" rel="noopener noreferrer" style={{ color: privacyPolicy?.urlColor }}>
								{privacyPolicy?.urlText}
							</a>
						</div>
					)}
				</div>
			</div>
			{!isSuper8 ? (
				isShowBottomInfo ? (
					<div className={classes.copyright}>
						<ImageDisplay
							style={{
								borderRadius: '50%',
								marginRight: 4,
							}}
							src={icSuperBotX}
							width={24}
							height={24}
						/>
						<a href={config.SUPERBOTX_URL} target="_blank" rel="noreferrer">
							{t('copyrigntSuperbotX')}
						</a>
					</div>
				) : null
			) : !hiddenPoweredBy ? (
				<div className={classes.copyright}>
					<ImageDisplay
						style={{
							borderRadius: '50%',
							marginRight: 4,
						}}
						src={icSuper8}
						width={24}
						height={24}
					/>
					<a href={config.SUPER8_URL} target="_blank" rel="noreferrer">
						{t('copyrignt')}
					</a>
				</div>
			) : null}
		</div>
	);
}

export default Preview;
